// Mutations do editor de templates (§4.5): salvar rascunho (POST
// versions), ativar versão (PUT activate — publicação E rollback) e
// publicar (POST + PUT encadeados) — com UPDATE OTIMISTA + rollback
// + toast, além do test-send (sem otimismo, exibe o resultado).
//
// Onda 2 (front-first): o gateway responde um write-ack ESTÁTICO
// (mocks/notification-center/write-ack.json), então NÃO invalidamos
// a query no sucesso — refetch traria o mock original e desfaria a
// escrita. Na onda 4 (writes reais) troca-se por invalidateQueries.
import { useTranslation } from "react-i18next"

import { useToast } from "@/hooks/use-toast"
import { useMutation, useQueryClient } from "@/libs"
import {
  postNotificationCenterTemplate,
  postNotificationCenterTemplateAiImprove,
  postNotificationCenterTemplateTestSend,
  postNotificationCenterTemplateVersion,
  putNotificationCenterTemplateVersionActivate,
} from "@/services/notification-center"
import type {
  NotificationTemplateAiImprovePayloadType,
  NotificationTemplateAiImproveResultType,
  NotificationTemplateCreatePayloadType,
  NotificationTemplateCreateResultType,
  NotificationTemplateTestSendPayloadType,
  NotificationTemplateTestSendResultType,
  NotificationTemplateVersionsResponseType,
  NotificationTemplateVersionType,
} from "@/types/notification-center"

import { getTemplateVersionsQueryKey } from "./hooks"

// Contexto de rollback compartilhado pelas mutations.
type VersionsSnapshotContext = {
  previous?: NotificationTemplateVersionsResponseType
}

// Próximo número de versão (max + 1).
export const getNextTemplateVersion = (
  versions: NotificationTemplateVersionType[]
): number =>
  versions.reduce(
    (max, item) => Math.max(max, item.version),
    0
  ) + 1

// Nova versão local (otimista) — o autor real vem do backend na
// onda 4; aqui marcamos como "você" via authorLabel do chamador.
const buildLocalVersion = (
  versions: NotificationTemplateVersionType[],
  source: string,
  author: string,
  status: NotificationTemplateVersionType["status"],
  active: boolean
): NotificationTemplateVersionType => {
  const now = new Date().toISOString()
  return {
    version: getNextTemplateVersion(versions),
    status,
    active,
    createdAt: now,
    updatedAt: now,
    author,
    source,
  }
}

// Marca UMA versão como ativa (PUBLISHED); a anteriormente ativa
// vira ARCHIVED (lifecycle V25 — nada é apagado).
const activateVersionInCache = (
  cache: NotificationTemplateVersionsResponseType,
  version: number
): NotificationTemplateVersionsResponseType => ({
  ...cache,
  versions: cache.versions.map((item) => {
    if (item.version === version) {
      return {
        ...item,
        status: "PUBLISHED",
        active: true,
      }
    }
    if (item.active) {
      return { ...item, status: "ARCHIVED", active: false }
    }
    return item
  }),
})

// Fábrica dos callbacks otimistas sobre a query de versões.
const useOptimisticVersionsWrite = <TVariables>(
  templateUuid: string,
  applyOptimistic: (
    cache: NotificationTemplateVersionsResponseType,
    variables: TVariables
  ) => NotificationTemplateVersionsResponseType,
  mutationFn: (variables: TVariables) => Promise<unknown>
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const queryKey = getTemplateVersionsQueryKey(templateUuid)

  return useMutation<
    unknown,
    Error,
    TVariables,
    VersionsSnapshotContext
  >({
    mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<NotificationTemplateVersionsResponseType>(
          queryKey
        )
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          applyOptimistic(previous, variables)
        )
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast({
        title: t(
          "pages.notificationCenter.templateEditorPage.writeError"
        ),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t(
          "pages.notificationCenter.templateEditorPage.writeSuccess"
        ),
      })
    },
  })
}

// POST …/versions — salva o source como NOVA versão DRAFT
// (nunca sobrescreve versão publicada).
export const useSaveTemplateDraft = (
  templateUuid: string,
  authorLabel: string
) =>
  useOptimisticVersionsWrite<{ source: string }>(
    templateUuid,
    (cache, { source }) => ({
      ...cache,
      versions: [
        buildLocalVersion(
          cache.versions,
          source,
          authorLabel,
          "DRAFT",
          false
        ),
        ...cache.versions,
      ],
    }),
    ({ source }) =>
      postNotificationCenterTemplateVersion(templateUuid, {
        source,
      })
  )

// PUT …/versions/:version/activate — rollback/ativação de versão
// existente (1 clique + AlertDialog; nada é apagado).
export const useActivateTemplateVersion = (
  templateUuid: string
) =>
  useOptimisticVersionsWrite<{ version: number }>(
    templateUuid,
    (cache, { version }) =>
      activateVersionInCache(cache, version),
    ({ version }) =>
      putNotificationCenterTemplateVersionActivate(
        templateUuid,
        version
      )
  )

// Publicar = POST nova versão + PUT activate ENCADEADOS (AlertDialog
// "Publicar vN e desativar vM?"). Otimista: nova versão já nasce
// PUBLISHED/ativa e a anterior é arquivada.
export const usePublishTemplateVersion = (
  templateUuid: string,
  authorLabel: string
) =>
  useOptimisticVersionsWrite<{
    source: string
    nextVersion: number
  }>(
    templateUuid,
    (cache, { source }) => {
      const withDraft: NotificationTemplateVersionsResponseType =
        {
          ...cache,
          versions: [
            buildLocalVersion(
              cache.versions,
              source,
              authorLabel,
              "DRAFT",
              false
            ),
            ...cache.versions,
          ],
        }
      return activateVersionInCache(
        withDraft,
        getNextTemplateVersion(cache.versions)
      )
    },
    async ({ source, nextVersion }) => {
      await postNotificationCenterTemplateVersion(
        templateUuid,
        { source }
      )
      return putNotificationCenterTemplateVersionActivate(
        templateUuid,
        nextVersion
      )
    }
  )

// Converte o resultado do POST /templates no shape da query de
// versões — semeia o cache p/ o editor abrir a v1 DRAFT direto,
// sem esperar o GET (mesma ideia do antigo seed do sentinela "new").
export const buildVersionsSeedFromCreateResult = (
  result: NotificationTemplateCreateResultType
): NotificationTemplateVersionsResponseType => ({
  uuid: result.uuid,
  whiteLabelId: result.whiteLabelId,
  name: result.name,
  channel: result.channel,
  usedBy: [],
  variables: [],
  versions: [
    {
      version: result.version,
      status: result.status,
      active: result.active,
      createdAt: result.createdAt,
      updatedAt: result.createdAt,
      author: "",
      source: "",
    },
  ],
})

// POST /notification-center/templates — cria o template real e
// semeia o cache de versões com a resposta (uuid REAL); a navegação
// p/ o editor fica no chamador (onSuccess do mutate).
export const useCreateTemplate = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation<
    NotificationTemplateCreateResultType,
    Error,
    NotificationTemplateCreatePayloadType
  >({
    mutationFn: (payload) =>
      postNotificationCenterTemplate(payload),
    onSuccess: (result) => {
      queryClient.setQueryData(
        getTemplateVersionsQueryKey(result.uuid),
        buildVersionsSeedFromCreateResult(result)
      )
    },
    onError: () => {
      toast({
        title: t(
          "pages.notificationCenter.templatesPage.createDialog.error"
        ),
        variant: "destructive",
      })
    },
  })
}

// POST …/ai-improve — pede à IA uma versão melhorada do source (sem
// otimismo e SEM invalidar cache: a sugestão volta pra UI, que decide
// aplicar — via setDraftSource no editor — ou descartar). Onda 2: a
// resposta é um MOCK de contrato; a IA real vem depois no backend.
export const useImproveTemplateWithAi = (
  templateUuid: string
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  return useMutation<
    NotificationTemplateAiImproveResultType,
    Error,
    NotificationTemplateAiImprovePayloadType
  >({
    mutationFn: (payload) =>
      postNotificationCenterTemplateAiImprove(
        templateUuid,
        payload
      ),
    onError: () => {
      toast({
        title: t(
          "pages.notificationCenter.templateEditorPage.ai.error"
        ),
        variant: "destructive",
      })
    },
  })
}

// POST …/test-send — envio de teste (sem otimismo; o resultado —
// testId + warning — é exibido no próprio dialog).
export const useTestSendTemplate = (
  templateUuid: string
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  return useMutation<
    NotificationTemplateTestSendResultType,
    Error,
    NotificationTemplateTestSendPayloadType
  >({
    mutationFn: (payload) =>
      postNotificationCenterTemplateTestSend(
        templateUuid,
        payload
      ),
    onError: () => {
      toast({
        title: t(
          "pages.notificationCenter.templateEditorPage.testSend.error"
        ),
        variant: "destructive",
      })
    },
  })
}
